<script setup>
import { computed } from 'vue';
import ButtonV4 from 'next/button/Button.vue';

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  isCurrentPlan: {
    type: Boolean,
    default: false,
  },
  billingCycle: {
    type: String,
    default: 'monthly',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

const price = computed(() => {
  const value =
    props.billingCycle === 'yearly'
      ? props.plan.yearly_price
      : props.plan.monthly_price;
  if (!value || value === 0) return 'Grátis';
  return `R$ ${parseFloat(value).toFixed(2).replace('.', ',')}`;
});

const yearlyDiscount = computed(() => {
  if (!props.plan.monthly_price || !props.plan.yearly_price) return 0;
  const monthlyAnnual = props.plan.monthly_price * 12;
  const discount =
    ((monthlyAnnual - props.plan.yearly_price) / monthlyAnnual) * 100;
  return Math.round(discount);
});

const formatLimit = value => {
  if (!value || value === 0) return '∞';
  if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
  return value.toString();
};

const features = computed(() => [
  {
    icon: 'i-lucide-users',
    label: 'agentes',
    value: formatLimit(props.plan.agent_limit),
  },
  {
    icon: 'i-lucide-inbox',
    label: 'caixas de entrada',
    value: formatLimit(props.plan.inbox_limit),
  },
  {
    icon: 'i-lucide-contact',
    label: 'contatos',
    value: formatLimit(props.plan.contacts_limit),
  },
  {
    icon: 'i-lucide-message-square',
    label: 'mensagens/mês',
    value: formatLimit(props.plan.messages_per_month),
  },
  {
    icon: 'i-lucide-hard-drive',
    label: 'armazenamento',
    value: props.plan.storage_mb
      ? `${formatLimit(props.plan.storage_mb)}MB`
      : '∞',
  },
  {
    icon: 'i-lucide-bot',
    label: 'automações',
    value: formatLimit(props.plan.automations_limit),
  },
  {
    icon: 'i-lucide-megaphone',
    label: 'campanhas',
    value: formatLimit(props.plan.campaigns_limit),
  },
  {
    icon: 'i-lucide-coins',
    label: 'créditos IA/mês',
    value: formatLimit(props.plan.included_credits),
  },
]);

const handleSelect = () => {
  emit('select', props.plan);
};
</script>

<template>
  <div
    class="relative flex flex-col p-6 rounded-2xl border transition-all h-full"
    :class="[
      isCurrentPlan
        ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
        : 'border-n-weak bg-n-solid-2 hover:border-n-slate-7',
    ]"
  >
    <!-- Badge -->
    <div
      v-if="plan.badge_text"
      class="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold text-white"
      :style="{ backgroundColor: plan.badge_color || '#6366f1' }"
    >
      {{ plan.badge_text }}
    </div>

    <!-- Current plan badge -->
    <div
      v-if="isCurrentPlan"
      class="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-woot-500 text-white text-xs font-medium"
    >
      {{ $t('BILLING_SETTINGS.PLANS.CURRENT') }}
    </div>

    <!-- Header -->
    <div class="text-center mb-4">
      <h3 class="text-lg font-bold text-n-slate-12">{{ plan.name }}</h3>
      <p class="text-sm text-n-slate-10 mt-1">{{ plan.description }}</p>
    </div>

    <!-- Price -->
    <div class="text-center mb-6">
      <div class="flex items-baseline justify-center gap-1">
        <span class="text-3xl font-bold text-n-slate-12">{{ price }}</span>
        <span v-if="plan.monthly_price > 0" class="text-sm text-n-slate-10">
          /{{
            billingCycle === 'yearly'
              ? $t('BILLING_SETTINGS.PLANS.YEAR')
              : $t('BILLING_SETTINGS.PLANS.MONTH')
          }}
        </span>
      </div>
      <div
        v-if="billingCycle === 'yearly' && yearlyDiscount > 0"
        class="mt-1 text-xs text-n-teal-11 font-medium"
      >
        {{ $t('BILLING_SETTINGS.PLANS.SAVE') }} {{ yearlyDiscount }}%
      </div>
    </div>

    <!-- Features -->
    <ul class="space-y-2 flex-1 mb-6">
      <li
        v-for="feature in features"
        :key="feature.label"
        class="flex items-center gap-2 text-sm"
      >
        <span class="i-lucide-check text-n-teal-10" />
        <span class="text-n-slate-11">
          <strong class="font-semibold text-n-slate-12">{{
            feature.value
          }}</strong>
          {{ feature.label }}
        </span>
      </li>
    </ul>

    <!-- Action -->
    <ButtonV4
      v-if="!isCurrentPlan"
      solid
      blue
      class="w-full"
      :is-loading="isLoading"
      @click="handleSelect"
    >
      {{ $t('BILLING_SETTINGS.PLANS.SELECT') }}
    </ButtonV4>
    <ButtonV4 v-else faded slate class="w-full" disabled>
      {{ $t('BILLING_SETTINGS.PLANS.CURRENT_PLAN') }}
    </ButtonV4>
  </div>
</template>
